import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from 'react-daisyui';
import { useAuthContext } from '@/context/AuthContext.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import { useLocation, useNavigate } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import TextInput from '@/components/functional/TextInput/TextInput.tsx';
import { resetPasswordSchema, ResetPasswordRequest } from '$/requests/ResetPasswordRequest.ts';

const ResetPasswordPage: FC = () => {
  const { t } = useTranslation();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const { control, handleSubmit } = useForm<ResetPasswordRequest>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
    },
  });
  const {
    resetPasswordQuery: {
      mutateAsync: resetPassword,
      isPending: isLoadingResetPassword,
      error: errorResetPassword,
      isError: isErrorResetPassword,
      isSuccess: isSuccessResetPassword,
    },
  } = useAuthContext();

  const isLoading = isLoadingResetPassword;
  const hasExpired = hash ? hash.includes('expired') : false;

  const handleResetPassword = (data: ResetPasswordRequest) => {
    void (async () => {
      try {
        if (isLoading) return;

        await resetPassword({
          password: data.password,
        });

        navigate(Routes.LOGIN);
      } catch (error) {
        console.error(error);
      }
    })();
  };

  const handleTryAgain = () => {
    navigate(Routes.FORGOT_PASSWORD);
  };

  return (
    <BrandCardSideContent>
      <form className="w-[343px] max-w-full flex flex-col gap-4" onSubmit={handleSubmit(handleResetPassword)}>
        <div className="flex flex-col gap-4">
          {isSuccessResetPassword && <Alert status="success">{t('resetPasswordPage.successInfo')}</Alert>}
          {isErrorResetPassword && <Alert status="error">{errorResetPassword?.message}</Alert>}
          {hasExpired && <Alert status="error">{t('resetPasswordPage.linkExpired')}</Alert>}
          <div className="flex flex-col gap-2 mb-2">
            <h1 className="font-feature font-bold text-2xl">{t('resetPasswordPage.header')}</h1>
            <p className="text-sm text-text-secondary">{t('resetPasswordPage.subheader')}</p>
          </div>
          <Controller
            control={control}
            name="password"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <TextInput
                error={errors.password?.message}
                value={value}
                onChange={onChange}
                disabled={isSuccessResetPassword || hasExpired}
                type="password"
                placeholder={t('placeholder.password')}
              />
            )}
          />
          {!hasExpired ? (
            <LoadingButton
              className="mt-2"
              loading={isLoading}
              disabled={isSuccessResetPassword || hasExpired}
              type="submit"
              color="primary"
            >
              {t('button.resetPassword')}
            </LoadingButton>
          ) : (
            <Button onClick={handleTryAgain} type="button" className="mt-2" color="primary">
              {t('button.tryAgain')}
            </Button>
          )}
        </div>
      </form>
    </BrandCardSideContent>
  );
};

export default ResetPasswordPage;
